/**
 * Flatten nested Firestore-like values into a single-level object for spreadsheets.
 */
import { relabelExportRowKeys } from './exportColumnLabels';

function isFirestoreLikeTimestamp(v) {
  return v != null && typeof v === 'object' && typeof v.toDate === 'function';
}

function serializeLeaf(value) {
  if (value === undefined || value === null) return '';
  if (isFirestoreLikeTimestamp(value)) {
    try {
      return value.toDate().toISOString();
    } catch {
      return '';
    }
  }
  if (Array.isArray(value)) {
    return value
      .map((item) => {
        if (item === null || item === undefined) return '';
        if (typeof item === 'object') return JSON.stringify(item);
        return String(item);
      })
      .join('; ');
  }
  if (typeof value === 'object') {
    try {
      return JSON.stringify(value);
    } catch {
      return String(value);
    }
  }
  return value;
}

/**
 * @param {Record<string, unknown>} obj
 * @param {string} prefix
 * @param {number} depth
 * @param {Record<string, string | number | boolean>} out
 * @param {number} maxDepth
 */
function walk(obj, prefix, depth, out, maxDepth) {
  if (obj === null || obj === undefined) {
    out[prefix || '_'] = '';
    return;
  }
  if (isFirestoreLikeTimestamp(obj)) {
    out[prefix] = String(serializeLeaf(obj));
    return;
  }
  if (Array.isArray(obj)) {
    out[prefix] = String(serializeLeaf(obj));
    return;
  }
  if (typeof obj !== 'object') {
    out[prefix] = obj;
    return;
  }
  if (depth >= maxDepth) {
    out[prefix] = String(serializeLeaf(obj));
    return;
  }
  const keys = Object.keys(obj);
  if (keys.length === 0) {
    out[prefix || '_empty'] = '{}';
    return;
  }
  for (const k of keys) {
    const path = prefix ? `${prefix}.${k}` : k;
    const v = obj[k];
    if (v !== null && typeof v === 'object' && !isFirestoreLikeTimestamp(v) && !Array.isArray(v)) {
      walk(v, path, depth + 1, out, maxDepth);
    } else {
      const leaf = serializeLeaf(v);
      out[path] = typeof leaf === 'string' || typeof leaf === 'number' || typeof leaf === 'boolean' ? leaf : String(leaf);
    }
  }
}

/**
 * @param {Record<string, unknown>} doc - raw Firestore fields (document id is not exported)
 * @returns {Record<string, string | number | boolean>}
 */
export function flattenDocForExportRow(doc) {
  const { userId: _omitUserId, ...rest } = doc;
  const out = {};
  walk(rest, '', 0, out, 12);
  return out;
}

/**
 * @param {Array<Record<string, unknown>>} documents
 * @param {{ transformFlatRow?: (row: Record<string, string | number | boolean>) => Record<string, string | number | boolean> }} [options]
 * @returns {Array<Record<string, string | number | boolean>>}
 */
export function alignExportRows(documents, options = {}) {
  const { transformFlatRow } = options;
  if (!documents.length) {
    return [relabelExportRowKeys({ _message: 'No documents in this collection.' })];
  }
  const flattened = documents.map((d) => {
    let row = flattenDocForExportRow(d);
    if (transformFlatRow) row = transformFlatRow(row);
    return row;
  });
  const keySet = new Set();
  flattened.forEach((r) => Object.keys(r).forEach((k) => keySet.add(k)));
  const keys = [...keySet].sort((a, b) => {
    const priority = ['participantEmail', 'participantDisplayName', 'userRole'];
    const rank = (k) => {
      const i = priority.indexOf(k);
      return i === -1 ? 1000 : i;
    };
    const ra = rank(a);
    const rb = rank(b);
    if (ra !== rb) return ra - rb;
    return a.localeCompare(b);
  });
  return flattened.map((r) => {
    const row = {};
    keys.forEach((k) => {
      if (k === 'userId') return;
      const v = r[k];
      row[k] = v !== undefined && v !== null ? v : '';
    });
    return relabelExportRowKeys(row);
  });
}
